// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — playing the data is recorded (owner, 2026-10-11: "any
 * playback action should automatically get recorded, including start time
 * and end time"). While the Studio is open, each stretch the dataset plays
 * becomes a **run**: the date it started from, the date it stopped on, and
 * how many seconds it played. The Studio turns a run into stops — one on the
 * start date, then a time run (`transition.data: 'timeRun'`) to the end date
 * that lasts as long as it played — so the video and the Khipu show it.
 *
 * Pure: the Studio feeds a sample a few times a second (playing or not, the
 * date on screen, the clock). The date on screen while paused is the start
 * of the next run: by the time a sample sees the play, a few frames may have
 * gone by. A date that goes back while playing (the loop came round, or a
 * seek back) closes the run there and starts another from the new date.
 */

export interface PlaybackSample {
  playing: boolean
  /** The data date on screen (epoch ms), or null without dates. */
  dateMs: number | null
  /** The dataset on the globe: a change closes the run. */
  datasetId: string | null
  /** The clock (ms). */
  nowMs: number
}

export type PlaybackEvent =
  /** The data started playing from `fromMs`; `pausedSeconds` since the last run ended (null: none before). */
  | { kind: 'start'; fromMs: number; pausedSeconds: number | null }
  /** It played from `fromMs` to `toMs` in `seconds`. */
  | { kind: 'run'; fromMs: number; toMs: number; seconds: number }

/** Shorter than this, or not moving the date, is not a run (a tap on play and pause). */
export const MIN_RUN_SECONDS = 0.3

interface Run {
  fromMs: number
  datasetId: string | null
  startWall: number
  lastMs: number
  lastWall: number
}

export interface PlaybackWatch {
  feed(sample: PlaybackSample): PlaybackEvent[]
  /** Forget everything (a new video, or the Studio closed). */
  reset(): void
}

export function createPlaybackWatch(): PlaybackWatch {
  let pausedMs: number | null = null
  let pausedDataset: string | null = null
  let run: Run | null = null
  let lastEndWall: number | null = null

  const close = (r: Run, toMs: number, wall: number): PlaybackEvent[] => {
    lastEndWall = wall
    const seconds = (wall - r.startWall) / 1000
    return toMs > r.fromMs && seconds >= MIN_RUN_SECONDS ? [{ kind: 'run', fromMs: r.fromMs, toMs, seconds }] : []
  }
  const open = (fromMs: number, s: PlaybackSample): PlaybackEvent => {
    run = { fromMs, datasetId: s.datasetId, startWall: s.nowMs, lastMs: s.dateMs ?? fromMs, lastWall: s.nowMs }
    return { kind: 'start', fromMs, pausedSeconds: lastEndWall === null ? null : (s.nowMs - lastEndWall) / 1000 }
  }

  return {
    feed(s) {
      const out: PlaybackEvent[] = []
      if (run && run.datasetId !== s.datasetId) {
        // Another dataset: what played of the first is not this one's run.
        run = null
        lastEndWall = null
      }
      if (!s.playing) {
        if (run) {
          const r = run
          run = null
          out.push(...close(r, s.dateMs ?? r.lastMs, s.nowMs))
        }
        pausedMs = s.dateMs
        pausedDataset = s.datasetId
        return out
      }
      if (s.dateMs === null) return out
      if (!run) {
        const from = pausedMs !== null && pausedDataset === s.datasetId && pausedMs <= s.dateMs ? pausedMs : s.dateMs
        out.push(open(from, s))
        return out
      }
      if (s.dateMs < run.lastMs) {
        // Back to an earlier date while playing: the run ends where it had got to.
        out.push(...close(run, run.lastMs, run.lastWall))
        out.push(open(s.dateMs, s))
        return out
      }
      run.lastMs = s.dateMs
      run.lastWall = s.nowMs
      return out
    },
    reset() {
      pausedMs = null
      pausedDataset = null
      run = null
      lastEndWall = null
    },
  }
}

/** The longest arrival a Khipu step may have (schema: transition.seconds ≤ 30). */
export const MAX_RUN_PIECE = 30
/** The shortest hold (schema: hold ≥ 0.5). */
export const MIN_HOLD = 0.5

/**
 * A run as the time runs of consecutive stops: one piece when it fits in an
 * arrival, else pieces of at most 30 s, each holding the shortest hold, the
 * dates shared out by time so the pace stays as it played.
 */
export function runPieces(fromMs: number, toMs: number, seconds: number): Array<{ atMs: number; seconds: number }> {
  if (seconds <= MAX_RUN_PIECE) return [{ atMs: toMs, seconds: round1(seconds) }]
  const count = Math.ceil((seconds + MIN_HOLD) / (MAX_RUN_PIECE + MIN_HOLD))
  // The holds between pieces are part of the time it played.
  const piece = (seconds - MIN_HOLD * (count - 1)) / count
  const pieces: Array<{ atMs: number; seconds: number }> = []
  for (let i = 1; i <= count; i++) {
    const elapsed = piece * i + MIN_HOLD * (i - 1)
    pieces.push({ atMs: i === count ? toMs : Math.round(fromMs + ((toMs - fromMs) * elapsed) / seconds), seconds: round1(piece) })
  }
  return pieces
}

const round1 = (v: number) => Math.max(0.1, Math.round(v * 10) / 10)
